import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { MsalInterceptor, MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';

/**
 * A wrapper around MsalInterceptor that prevents token acquisition when no accounts exist.
 *
 * This prevents the interceptor from triggering a redirect (or popup) that conflicts
 * with the MsalGuard which is responsible for initiating the login flow.
 *
 * The problem this solves:
 * 1. App loads, MSAL initializes
 * 2. TranslateLoader (or other service) makes an HTTP request to a protected endpoint
 * 3. MsalInterceptor tries acquireTokenSilent → fails (no account)
 * 4. MsalInterceptor falls back to redirect → conflicts with Guard → login loop
 *
 * Solution:
 * - If no accounts exist, let the request pass through without MSAL
 * - The request will fail with 401 (expected)
 * - The Guard will handle the login redirect properly
 * - After login, requests will work normally
 */
@Injectable()
export class SafeMsalInterceptor implements HttpInterceptor {
    private readonly msalService = inject(MsalService);
    private readonly msalInterceptor = inject(MsalInterceptor);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const accounts = this.msalService.instance.getAllAccounts();

        // If no accounts exist, skip MSAL token acquisition
        // Let the request go through without auth - it will fail with 401 if protected
        // The Guard will handle redirecting to login
        if (accounts.length === 0) {
            console.log('[SafeMsalInterceptor] No accounts - passing request through without MSAL');
            return next.handle(req);
        }

        // Accounts exist - use the normal MSAL interceptor
        return this.msalInterceptor.intercept(req, next);
    }
}
